"use client";

import { useEffect, useRef } from "react";
import { Toaster } from "sonner";

/**
 * Native modal `<dialog>` elements render in the browser top layer, above every
 * normal portal — including Sonner's. To keep toasts visible above open modals
 * we mount the Toaster inside a permanently-open, manual popover: popovers are
 * promoted to the top layer without being modal, so the page stays interactive.
 *
 * The host is a full-viewport, `pointer-events-none` element (Sonner re-enables
 * pointer events on the toasts themselves), so it never blocks clicks.
 */

let hostEl: HTMLElement | null = null;

function supportsPopover(el: HTMLElement | null): el is HTMLElement {
  return !!el && typeof el.showPopover === "function";
}

/**
 * Re-promote the toast host to the top of the top layer. The top layer stacks by
 * promotion order, so a modal opened after mount would otherwise cover the
 * toasts; re-showing the popover moves it back above the current modal stack.
 *
 * The hide/show pair runs synchronously so `display` toggles within a single
 * frame, and it does not unmount the Toaster's children, so in-flight toast
 * animations are not restarted.
 */
export function bringToastLayerToFront() {
  const el = hostEl;
  if (!supportsPopover(el)) return;
  try {
    if (el.matches(":popover-open")) {
      // Already topmost? Skip the redundant toggle (e.g. stacked modals opening
      // in sequence would otherwise flicker the corner needlessly).
      el.hidePopover();
    }
    el.showPopover();
  } catch {
    // Ignore InvalidStateError races; the layer stays where it is.
  }
}

export function ToastLayer() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!supportsPopover(el)) return;
    hostEl = el;
    try {
      el.showPopover();
    } catch {
      // ignore
    }
    return () => {
      if (hostEl === el) hostEl = null;
    };
  }, []);

  return (
    // `popover="manual"` keeps it open until we hide it (never on Esc/click).
    <div ref={ref} popover="manual" className="ha-toast-layer">
      <Toaster />
    </div>
  );
}
